import { A, useNavigate } from "@solidjs/router";
import { useQuery } from "@tanstack/solid-query";
import {
  type WebsiteHealthSummary,
  WebsiteHealthSummaryListSchema,
} from "contracts/api/website/responses";
import { createSignal, For, Show } from "solid-js";
import { SCAN_AVAILABILITY_KEY } from "../../enums/scan";
import { KEYBOARD_SCOPE } from "../../enums/shell";
import { setScope } from "../../keyboard/manager";
import { isAdmin } from "../../store/admin";
import { api } from "../../store/api";
import { Delta } from "../../ui/Delta";
import { FaroLighthouseMark } from "../../ui/FaroLighthouseMark";
import { relativeAge } from "../../ui/format";
import { healthLabel, healthVar } from "../../ui/health";
import { AddWebsitePanel } from "../admin/AddWebsitePanel";
import { AdminPanel } from "../admin/AdminPanel";
import { websiteScanAvailability } from "../scan/scanAvailability";

export function Landing() {
  setScope(KEYBOARD_SCOPE.LIST);
  const websites = useQuery(() => ({
    queryKey: ["websites"],
    queryFn: async () =>
      WebsiteHealthSummaryListSchema.parse(await api.listWebsites()),
  }));
  const [adding, setAdding] = createSignal(false);
  const [admin, setAdmin] = createSignal(false);

  return (
    <main class="landing">
      <div class="landing-bar">
        <FaroLighthouseMark class="landing-beacon" size={24} />
        <span class="landing-brand">Faro</span>
      </div>
      <div class="landing-main">
        <div class="landing-head-row">
          <div>
            <h1 class="landing-h1">Monitored websites</h1>
            <p class="landing-sub">
              Signal health across every property you track.
            </p>
          </div>
          <div class="landing-head-row">
            <A href="/playground" class="btn">
              Playground
            </A>
            <A href="/evals" class="btn">
              Evals
            </A>
            <Show when={isAdmin()}>
              <button class="btn" type="button" onClick={() => setAdmin(true)}>
                Admin
              </button>
            </Show>
            <button
              class="btn btn-primary"
              type="button"
              onClick={() => setAdding(true)}
            >
              Add website
            </button>
          </div>
        </div>
        <div class="tbl-wrap card">
          <Show
            when={websites.data}
            fallback={<div class="loading">Loading websites…</div>}
          >
            {(rows) => (
              <Show
                when={rows().length > 0}
                fallback={
                  <div class="empty">
                    <div class="empty-title">No websites yet</div>
                  </div>
                }
              >
                <WebsiteTable rows={rows()} />
              </Show>
            )}
          </Show>
        </div>
      </div>
      <Show when={adding()}>
        <AddWebsitePanel onClose={() => setAdding(false)} />
      </Show>
      <Show when={admin()}>
        <AdminPanel
          websites={websites.data ?? []}
          onClose={() => setAdmin(false)}
        />
      </Show>
    </main>
  );
}

function WebsiteTable(props: { rows: WebsiteHealthSummary[] }) {
  return (
    <table class="tbl tbl-link">
      <thead>
        <tr>
          <th>Website</th>
          <th class="td-num">Content Fitness</th>
          <th>Health</th>
          <th class="td-num">Open</th>
          <th>Last run</th>
        </tr>
      </thead>
      <tbody>
        <For each={props.rows}>
          {(summary) => <WebsiteRow summary={summary} />}
        </For>
      </tbody>
    </table>
  );
}

function WebsiteRow(props: { summary: WebsiteHealthSummary }) {
  const navigate = useNavigate();
  const name = () => props.summary.displayName ?? props.summary.clientName;
  const scanState = () => websiteScanAvailability(props.summary);
  return (
    <tr
      class="tbl-row"
      onClick={() => navigate(`/w/${props.summary.websiteId}`)}
    >
      <td class="td-issue">
        <div class="td-site">
          <span class="site-fav">{name().slice(0, 1)}</span>
          <div>
            <div class="td-issue-title">{name()}</div>
            <div class="td-issue-sub mono">{props.summary.domain}</div>
          </div>
        </div>
      </td>
      <td class="td-num">
        <span class="score-pill mono">
          {props.summary.signalScore === null ? "—" : props.summary.signalScore}
        </span>
        <Show when={props.summary.scoreRunFinishedAt}>
          {(finishedAt) => (
            <div class="td-muted">Scored {relativeAge(finishedAt())}</div>
          )}
        </Show>
      </td>
      <td>
        <span class="health-cell">
          <span
            class="tile-dot"
            style={{ background: healthVar(props.summary.healthState) }}
          />
          <span>{healthLabel(props.summary.healthState)}</span>
          <Delta value={props.summary.scoreDelta} />
        </span>
      </td>
      <td class="td-num mono">{props.summary.openIssueCount}</td>
      <td>
        <span
          class={`badge ${scanState().key === SCAN_AVAILABILITY_KEY.IN_PROGRESS ? "badge-status st-in_progress" : ""}`}
          title={scanState().detail}
        >
          {scanState().label}
        </span>
      </td>
    </tr>
  );
}
